import React, { useEffect, useMemo, useState } from 'react';
import { ActivityLogEntry, User } from '../../../types';
import { AdminView } from '../../../enums';
import { apiClient } from '../../../services/apiClient';
import { Filter, RefreshCw } from 'lucide-react';

interface TraceViewProps {
  currentUser: User;
}

export const TraceView: React.FC<TraceViewProps> = ({ currentUser }) => {
  const [logs, setLogs] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  const [context, setContext] = useState('master_data_table');
  const [eventType, setEventType] = useState('');
  const [userId, setUserId] = useState('');

  const knownEvents = useMemo(
    () => [
      { value: '', label: 'All Events' },
      { value: 'MASTER_TABLE_COLUMNS_UPDATED', label: 'Master Data – Columns Updated' },
      { value: 'MASTER_TABLE_EXPORT_FULL', label: 'Master Data – Export (Full Data)' },
      { value: 'MASTER_TABLE_EXPORT_CURRENT_VIEW', label: 'Master Data – Export (Current View)' },
    ],
    []
  );

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.activityLog.list({
        context: context || undefined,
        event_type: eventType || undefined,
        user_id: userId || undefined,
        page,
        pageSize,
      });
      setLogs(res.items || []);
      setTotal(res.totalCount || 0);
    } catch (e) {
      console.warn('Failed to load activity log', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context, eventType, userId, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-zinc-900">Trace / Track</h2>
          <p className="text-sm text-zinc-600">Audit trail for Master Data actions.</p>
        </div>
        <button
          onClick={fetchLogs}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-zinc-700 border border-zinc-200 hover:bg-zinc-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="bg-white p-4 rounded-2xl border border-zinc-200 shadow-sm flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 text-zinc-400 font-bold text-sm pr-2">
          <Filter className="w-4 h-4" /> Filters
        </div>
        <select value={context} onChange={(e) => { setPage(1); setContext(e.target.value); }} className="bg-zinc-50 border border-zinc-200 text-zinc-700 text-sm rounded-lg p-2 outline-none focus:ring-2 focus:ring-teal-100 font-semibold">
          <option value="">All Contexts</option>
          <option value="master_data_table">Master Data Table</option>
        </select>
        <select value={eventType} onChange={(e) => { setPage(1); setEventType(e.target.value); }} className="bg-zinc-50 border border-zinc-200 text-zinc-700 text-sm rounded-lg p-2 outline-none focus:ring-2 focus:ring-teal-100 font-semibold">
          {knownEvents.map((e) => (
            <option key={e.value || 'all'} value={e.value}>{e.label}</option>
          ))}
        </select>
        <input
          value={userId}
          onChange={(e) => { setPage(1); setUserId(e.target.value); }}
          placeholder="User ID (optional)"
          className="bg-zinc-50 border border-zinc-200 text-zinc-700 text-sm rounded-lg p-2 outline-none focus:ring-2 focus:ring-teal-100 font-semibold"
        />
        <span className="text-xs text-zinc-500 ml-auto">Showing {logs.length} of {total}</span>
      </div>

      <div className="bg-white rounded-3xl border border-zinc-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left whitespace-nowrap">
            <thead className="bg-zinc-50 text-zinc-500 font-bold uppercase text-xs border-b border-zinc-200">
              <tr>
                <th className="px-6 py-3">Time</th>
                <th className="px-6 py-3">User</th>
                <th className="px-6 py-3">Event</th>
                <th className="px-6 py-3">Context</th>
                <th className="px-6 py-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {logs.length === 0 ? (
                <tr><td colSpan={5} className="p-6 text-center text-zinc-400">No activity yet.</td></tr>
              ) : logs.map((log) => (
                <tr key={log.id} className="hover:bg-zinc-50">
                  <td className="px-6 py-3 text-xs text-zinc-500">{log.createdAt}</td>
                  <td className="px-6 py-3 text-sm font-semibold text-zinc-800">{log.userName || log.userId}</td>
                  <td className="px-6 py-3 text-xs font-mono text-zinc-700">{log.eventType}</td>
                  <td className="px-6 py-3 text-xs text-zinc-500">{log.context || '-'}</td>
                  <td className="px-6 py-3 text-xs text-zinc-600 max-w-xl break-all">
                    {log.payload ? JSON.stringify(log.payload) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-6 py-3 bg-zinc-50 border-t border-zinc-200 text-xs text-zinc-600">
          <span>Page {page} / {totalPages}</span>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1 rounded border border-zinc-200 disabled:opacity-40">Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="px-3 py-1 rounded border border-zinc-200 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
};
